/**
 * Gets the avatar URL for a user, checking the avatar custom field first.
 *
 * Arcadier's Media array accumulates all uploaded images and returns them in
 * non-deterministic order with no timestamps. The custom field provides a reliable
 * single source of truth for the current avatar.
 *
 * Priority:
 * 1. Avatar custom field — -ProfileAvatarUrl- or -PrivateAvatarUrl- (set on upload)
 * 2. Media array fallback — last item (for users who haven't uploaded since custom field was added)
 *
 * @param {Object|Array} userOrMedia - Either a user object with CustomFields/Media, or just a Media array
 * @param {string} [fallback=''] - Fallback URL if no valid media exists
 * @returns {string} The avatar URL, or fallback
 */
export function getLatestMediaUrl(userOrMedia, fallback = '') {
    // Handle null/undefined
    if (!userOrMedia) {
        return fallback;
    }

    // If passed an array directly, treat it as Media array (legacy behavior)
    if (Array.isArray(userOrMedia)) {
        return getMediaUrlFromArray(userOrMedia, fallback);
    }

    // If passed an object, check for custom field first
    if (typeof userOrMedia === 'object') {
        // Check avatar custom field first (field may be named -ProfileAvatarUrl- or -PrivateAvatarUrl- depending on marketplace)
        const customFields = userOrMedia.CustomFields;
        if (customFields && Array.isArray(customFields)) {
            const avatarField = customFields.find(cf => cf.Code && (cf.Code.includes('-ProfileAvatarUrl-') || cf.Code.includes('-PrivateAvatarUrl-')));
            if (avatarField?.Values?.[0]) {
                return avatarField.Values[0];
            }
        }

        // Fall back to Media array
        if (userOrMedia.Media) {
            return getMediaUrlFromArray(userOrMedia.Media, fallback);
        }
    }

    return fallback;
}

/**
 * Extracts URL from a Media array, preferring items with valid IDs.
 * Falls back to last item (matches master's dominant Media[length-1] pattern).
 * @private
 */
function getMediaUrlFromArray(mediaArray, fallback = '') {
    if (!mediaArray || !Array.isArray(mediaArray) || mediaArray.length === 0) {
        return fallback;
    }

    // Prefer media with a valid ID (non-null, non-"null")
    const mediaWithId = mediaArray.find(m => m.ID && m.ID !== 'null');
    // Fall back to last item — matches master's Media[length-1] behavior (most-recent-ish)
    const selectedMedia = mediaWithId || mediaArray[mediaArray.length - 1];

    return selectedMedia?.MediaUrl || fallback;
}

export default getLatestMediaUrl;
